import React, { useState, useEffect, useCallback, useRef } from "react";
import { Send, Lock, ShieldAlert, Trash2, CheckSquare, Square, X, ArrowDown, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const MAX_CACHE_SIZE = 600;
const decryptionCache = new Map();
let cryptoKeyCache = null;
let currentKeySecret = null;

function setDecryptedCache(cacheKey, plain) {
  if (decryptionCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = decryptionCache.keys().next().value;
    decryptionCache.delete(oldestKey);
  }
  decryptionCache.set(cacheKey, plain);
}

// =========================================================
// APPLICATION-LEVEL AES-256-GCM ENCRYPTION ENGINE
// =========================================================
async function getAESKey(secret) {
  if (!secret) return null;
  if (cryptoKeyCache && currentKeySecret === secret) return cryptoKeyCache;
  if (!window.crypto || !window.crypto.subtle) return null;

  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  const derived = await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode("expolead_aes_gcm_2026"),
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );

  cryptoKeyCache = derived;
  currentKeySecret = secret;
  return derived;
}

async function encryptMessage(text, secret) {
  const key = await getAESKey(secret);
  if (!key) throw new Error("CRYPTO_KEY_UNAVAILABLE");

  const enc = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertextBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(text)
  );

  const combined = new Uint8Array(iv.length + ciphertextBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertextBuffer), iv.length);

  let binary = "";
  for (let i = 0; i < combined.byteLength; i++) {
    binary += String.fromCharCode(combined[i]);
  }

  return "enc:v2:" + btoa(binary);
}

async function decryptMessage(cipherText, secret) {
  if (!cipherText || typeof cipherText !== "string") return "";
  if (!cipherText.startsWith("enc:")) return cipherText;

  // Secret/version-aware cache key
  const secretHash = secret ? secret.slice(0, 10) : "default";
  const cacheKey = `${secretHash}:::${cipherText}`;
  if (decryptionCache.has(cacheKey)) return decryptionCache.get(cacheKey);

  try {
    const isV2 = cipherText.startsWith("enc:v2:");
    const rawB64 = isV2 ? cipherText.slice(7) : cipherText.slice(4);
    const binary = atob(rawB64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    if (bytes.length < 28) return "[Encrypted message]";

    const iv = bytes.slice(0, 12);
    const encryptedData = bytes.slice(12);

    const key = await getAESKey(secret);
    if (!key) return "[Encrypted message]";

    const decrypted = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encryptedData
    );

    const plain = new TextDecoder().decode(decrypted);
    setDecryptedCache(cacheKey, plain);
    return plain;
  } catch (err) {
    return "[Encrypted message]";
  }
}

function parseMessageDate(val) {
  if (!val) return new Date();
  if (val instanceof Date) return isNaN(val.getTime()) ? new Date() : val;

  const str = String(val).trim();
  if (!str) return new Date();

  if (/^\d+(\.\d+)?$/.test(str)) {
    const num = Number(str);
    return new Date(num > 1e11 ? num : num * 1000);
  }

  const normalized = str.includes(" ") && !str.includes("T") ? str.replace(" ", "T") : str;
  let d = new Date(normalized);

  if (isNaN(d.getTime())) d = new Date(str);
  if (isNaN(d.getTime())) return new Date();

  const now = Date.now();
  if (d.getTime() - now > 60000) {
    const stripped = normalized.replace(/[Zz]$/, "").replace(/[+-]\d{2}:?\d{2}$/, "");
    const localD = new Date(stripped);
    if (!isNaN(localD.getTime()) && localD.getTime() <= now + 60000) {
      return localD;
    }
  }

  return d;
}

function formatTime(isoStr) {
  if (!isoStr) return "Just now";
  try {
    const d = parseMessageDate(isoStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "Just now";
  }
}

function reconcileIncomingMessages(prevList, incomingList, currentUserId) {
  let updated = [...prevList];
  let hasChanges = false;

  for (const incoming of incomingList) {
    if (!incoming || (!incoming.id && !incoming.client_message_id)) continue;

    const exactIdIdx = updated.findIndex((m) => String(m.id) === String(incoming.id));
    if (exactIdIdx !== -1) {
      if (updated[exactIdIdx].id !== incoming.id || updated[exactIdIdx].text !== incoming.text) {
        updated[exactIdIdx] = { ...updated[exactIdIdx], ...incoming };
        hasChanges = true;
      }
      continue;
    }

    const incomingCid = incoming.client_message_id;
    const clientMsgIdx = incomingCid
      ? updated.findIndex((m) => m.client_message_id === incomingCid || m.id === incomingCid)
      : -1;

    if (clientMsgIdx !== -1) {
      updated[clientMsgIdx] = { ...updated[clientMsgIdx], ...incoming };
      hasChanges = true;
      continue;
    }

    const isFromMe = String(incoming.sender_id) === String(currentUserId);
    if (isFromMe) {
      const optIdx = updated.findIndex((m) => {
        const isOptimistic = String(m.id).startsWith("cid_") || m.is_optimistic;
        return isOptimistic && m.text === incoming.text;
      });

      if (optIdx !== -1) {
        updated[optIdx] = {
          ...incoming,
          client_message_id: updated[optIdx].client_message_id || incomingCid
        };
        hasChanges = true;
        continue;
      }
    }

    updated.push(incoming);
    hasChanges = true;
  }

  return hasChanges ? updated : prevList;
}

function getResolvedWsUrl(ticketParam) {
  let wsHost = window.location.host;
  let wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";

  const baseURL = api.defaults?.baseURL || "";
  if (baseURL && baseURL.startsWith("http")) {
    try {
      const parsed = new URL(baseURL);
      wsHost = parsed.host;
      wsProtocol = parsed.protocol === "https:" ? "wss:" : "ws:";
    } catch (e) {}
  }

  return `${wsProtocol}//${wsHost}/api/dashboard/ws/chat?ticket=${encodeURIComponent(ticketParam)}`;
}

export default function TeamChat({ user, companyName, hasCompany, activeTab, onNewMessageReceived }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [hasUnreadBelow, setHasUnreadBelow] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("connecting"); // 'connected' | 'connecting' | 'offline'

  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const isConnectingRef = useRef(false);
  const reconnectAttempts = useRef(0);
  const serverSecretRef = useRef(null);
  const isKeyReadyRef = useRef(false);

  const isScrolledUpRef = useRef(isScrolledUp);
  const activeTabRef = useRef(activeTab);
  const onNewMessageReceivedRef = useRef(onNewMessageReceived);
  const userRef = useRef(user);

  useEffect(() => { isScrolledUpRef.current = isScrolledUp; }, [isScrolledUp]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { onNewMessageReceivedRef.current = onNewMessageReceived; }, [onNewMessageReceived]);
  useEffect(() => { userRef.current = user; }, [user]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior
      });
      setHasUnreadBelow(false);
      setIsScrolledUp(false);
    }
  }, []);

  // Explicit delta sync to catch missed messages upon reconnect
  const triggerDeltaSync = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/chat", { params: { limit: 25 } });
      if (!Array.isArray(data) || data.length === 0) return;

      const resolved = await Promise.all(
        data.map(async (m) => ({
          ...m,
          text: await decryptMessage(m.text, serverSecretRef.current)
        }))
      );

      setMessages((prev) => reconcileIncomingMessages(prev, resolved, userRef.current?.id));
    } catch (err) {
      // Silent background sync
    }
  }, []);

  // 1. Initial Load: Fetch Encryption Secret and Initial 50 Messages
  useEffect(() => {
    if (!hasCompany) return;
    let isCancelled = false;

    (async () => {
      try {
        const keyRes = await api.get("/dashboard/chat/key").catch(() => ({ data: { key: null } }));
        if (isCancelled) return;
        serverSecretRef.current = keyRes.data?.key || companyName;
        isKeyReadyRef.current = true;

        const { data } = await api.get("/dashboard/chat", { params: { limit: 50 } });
        if (isCancelled || !Array.isArray(data)) return;

        const resolved = await Promise.all(
          data.map(async (m) => ({
            ...m,
            text: await decryptMessage(m.text, serverSecretRef.current)
          }))
        );

        if (!isCancelled) {
          const uniqueList = [];
          const seen = new Set();
          for (const item of resolved) {
            const idKey = String(item.id);
            if (!seen.has(idKey)) {
              seen.add(idKey);
              uniqueList.push(item);
            }
          }

          setMessages(uniqueList);
          setHasMoreHistory(data.length === 50);
          setTimeout(() => scrollToBottom("instant"), 40);
        }
      } catch (err) {
        console.warn("Chat setup notice:", err);
      }
    })();

    return () => { isCancelled = true; };
  }, [hasCompany, companyName, scrollToBottom]);

  // 2. Single-Owner WebSocket Lifecycle (With Heartbeat & Jittered Backoff)
  useEffect(() => {
    if (!hasCompany) return;
    let reconnectTimer = null;
    let heartbeatTimer = null;
    let closedExplicitly = false;

    const connect = async () => {
      if (closedExplicitly || isConnectingRef.current) return;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

      // Gate: Key must be ready before connecting
      if (!isKeyReadyRef.current) {
        reconnectTimer = setTimeout(connect, 300);
        return;
      }

      isConnectingRef.current = true;
      setConnectionStatus("connecting");

      try {
        const ticketRes = await api.post("/dashboard/ws/ticket").catch(() => null);
        const ticket = ticketRes?.data?.ticket;

        if (!ticket || closedExplicitly) {
          isConnectingRef.current = false;
          setConnectionStatus("offline");
          scheduleReconnect();
          return;
        }

        const wsUrl = getResolvedWsUrl(ticket);
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          isConnectingRef.current = false;
          reconnectAttempts.current = 0;
          setConnectionStatus("connected");

          // Catch up on any messages missed during disconnect
          triggerDeltaSync();

          // Active 20-second keepalive ping
          heartbeatTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "ping" }));
            }
          }, 20000);
        };

        ws.onmessage = async (evt) => {
          try {
            const payload = JSON.parse(evt.data);
            if (payload.type === "pong") return;

            const isChatMessage =
              payload.type === "message" ||
              payload.type === "chat" ||
              (!payload.type && (payload.text || payload.message));

            if (isChatMessage) {
              const rawText = payload.text || payload.message || "";
              const plain = await decryptMessage(rawText, serverSecretRef.current);
              const formatted = { ...payload, text: plain };

              const isFromMe = String(payload.sender_id) === String(userRef.current?.id);
              if (!isFromMe) {
                if (activeTabRef.current !== "chat" && onNewMessageReceivedRef.current) {
                  onNewMessageReceivedRef.current();
                }
                if (isScrolledUpRef.current) {
                  setHasUnreadBelow(true);
                }
              }

              setMessages((prev) => reconcileIncomingMessages(prev, [formatted], userRef.current?.id));

              if (!isScrolledUpRef.current) {
                setTimeout(() => scrollToBottom("smooth"), 30);
              }
            }
          } catch (err) {
            console.error("WS parse error:", err);
          }
        };

        ws.onclose = () => {
          isConnectingRef.current = false;
          setConnectionStatus("offline");
          if (heartbeatTimer) clearInterval(heartbeatTimer);
          if (closedExplicitly) return;
          scheduleReconnect();
        };

        ws.onerror = () => {
          isConnectingRef.current = false;
          setConnectionStatus("offline");
        };
      } catch (err) {
        isConnectingRef.current = false;
        setConnectionStatus("offline");
        scheduleReconnect();
      }
    };

    const scheduleReconnect = () => {
      if (closedExplicitly) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);

      // Exponential backoff with random jitter (prevents thundering herd)
      const baseDelay = Math.min(1000 * 1.5 ** reconnectAttempts.current, 10000);
      const jitter = Math.random() * 1000;
      const totalDelay = Math.floor(baseDelay + jitter);

      reconnectAttempts.current += 1;
      reconnectTimer = setTimeout(connect, totalDelay);
    };

    connect();

    const handleAppResume = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        connect();
      } else {
        triggerDeltaSync();
      }
    };

    window.addEventListener("online", handleAppResume);
    document.addEventListener("visibilitychange", handleAppResume);

    return () => {
      closedExplicitly = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      window.removeEventListener("online", handleAppResume);
      document.removeEventListener("visibilitychange", handleAppResume);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [hasCompany, scrollToBottom, triggerDeltaSync]);

  // 3. Fallback Delta Sync: Only active when WebSocket is disconnected
  useEffect(() => {
    if (!hasCompany) return;

    const interval = setInterval(() => {
      if (connectionStatus !== "connected" && !document.hidden) {
        triggerDeltaSync();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [hasCompany, connectionStatus, triggerDeltaSync]);

  // 4. Cursor History Pagination
  const loadOlderMessages = async () => {
    if (isLoadingHistory || !hasMoreHistory || messages.length === 0) return;
    setIsLoadingHistory(true);

    const oldest = messages[0];
    const container = containerRef.current;
    const oldScrollHeight = container ? container.scrollHeight : 0;

    try {
      const { data } = await api.get("/dashboard/chat", {
        params: {
          limit: 50,
          before: oldest.created_at,
          before_id: oldest.id
        }
      });

      if (!Array.isArray(data) || data.length === 0) {
        setHasMoreHistory(false);
        return;
      }

      const resolved = await Promise.all(
        data.map(async (m) => ({
          ...m,
          text: await decryptMessage(m.text, serverSecretRef.current)
        }))
      );

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => String(m.id)));
        const filtered = resolved.filter((m) => !existingIds.has(String(m.id)));
        return [...filtered, ...prev];
      });

      if (data.length < 50) setHasMoreHistory(false);

      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - oldScrollHeight;
        }
      });
    } catch (err) {
      toast.error("Failed to load older messages");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const up = scrollHeight - scrollTop - clientHeight > 80;
    setIsScrolledUp(up);
    if (!up) setHasUnreadBelow(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const raw = inputText.trim();
    if (!raw || isSending) return;

    if (raw.length > 5000) {
      toast.error("Message exceeds maximum allowed length (5000 characters).");
      return;
    }

    const secret = serverSecretRef.current || companyName;
    let cipher;
    try {
      cipher = await encryptMessage(raw, secret);
    } catch {
      toast.error("Encryption error. Message blocked from plaintext dispatch.");
      return;
    }

    const clientMsgId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `cid_${Date.now()}_${Math.random()}`;

    const optimistic = {
      id: clientMsgId,
      client_message_id: clientMsgId,
      sender: user?.name || "You",
      sender_id: user?.id,
      text: raw,
      created_at: new Date().toISOString(),
      is_optimistic: true
    };

    setInputText("");
    setIsSending(true);
    setMessages((prev) => [...prev, optimistic]);
    setTimeout(() => scrollToBottom("smooth"), 20);

    try {
      const res = await api.post("/dashboard/chat", {
        text: cipher,
        client_message_id: clientMsgId
      });

      if (res?.data) {
        const serverMsg = res.data;
        const serverId = serverMsg.id || serverMsg.message_id;

        if (serverId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.client_message_id === clientMsgId || m.id === clientMsgId
                ? {
                    ...m,
                    ...serverMsg,
                    id: serverId,
                    client_message_id: clientMsgId,
                    text: raw,
                    is_optimistic: false
                  }
                : m
            )
          );
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to deliver message.");
      setMessages((prev) => prev.filter((m) => m.client_message_id !== clientMsgId && m.id !== clientMsgId));
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (id) => {
    const previous = [...messages];
    setMessages((prev) => prev.filter((m) => m.id !== id && m.client_message_id !== id));

    try {
      await api.delete(`/dashboard/chat/${id}`);
    } catch (err) {
      setMessages(previous);
      toast.error("Delete failed. Please check connection.");
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const previous = [...messages];
    setMessages((prev) => prev.filter((m) => !selectedIds.has(m.id) && !selectedIds.has(m.client_message_id)));
    setSelectedIds(new Set());
    setIsSelectMode(false);

    try {
      await api.post("/dashboard/chat/delete-bulk", { message_ids: ids });
    } catch (err) {
      setMessages(previous);
      toast.error("One or more messages could not be deleted.");
    }
  };

  if (!hasCompany) {
    return (
      <div className="p-8 bg-white border border-slate-200 rounded-[28px] text-center space-y-2">
        <ShieldAlert className="w-8 h-8 text-amber-500 mx-auto" />
        <h3 className="font-bold text-slate-900 text-sm">Company Name Required</h3>
        <p className="text-xs text-slate-500">Configure your company profile to use encrypted team chat.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-[28px] shadow-sm overflow-hidden flex flex-col h-[540px] relative">
      {/* HEADER */}
      <div className="px-5 py-3.5 bg-slate-50/90 border-b border-slate-200 flex items-center justify-between">
        {isSelectMode ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <button onClick={() => { setIsSelectMode(false); setSelectedIds(new Set()); }} className="p-1 text-slate-500">
                <X className="w-4 h-4" />
              </button>
              <span className="text-xs font-bold">{selectedIds.size} Selected</span>
            </div>
            <button onClick={handleDeleteSelected} disabled={selectedIds.size === 0} className="px-3 py-1 bg-rose-600 text-white rounded-lg text-xs font-bold disabled:opacity-50 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Delete for Me
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                <Lock className="w-3.5 h-3.5" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-bold text-slate-900 leading-tight truncate max-w-[140px]">{companyName}</h3>
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${
                      connectionStatus === "connected" ? "bg-emerald-500" : connectionStatus === "connecting" ? "bg-amber-500 animate-pulse" : "bg-rose-500"
                    }`}
                    title={`Status: ${connectionStatus}`}
                  />
                </div>
              </div>
            </div>
            {messages.length > 0 && (
              <button onClick={() => setIsSelectMode(true)} className="p-1.5 text-slate-400 hover:text-slate-700">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* CHAT STREAM */}
      <div ref={containerRef} onScroll={handleScroll} className="flex-1 p-4 overflow-y-auto space-y-3 bg-[#f8fafc]">
        {hasMoreHistory && messages.length > 0 && (
          <div className="text-center pt-1 pb-2">
            <button
              onClick={loadOlderMessages}
              disabled={isLoadingHistory}
              className="text-[11px] font-semibold text-blue-600 hover:underline disabled:opacity-50"
            >
              {isLoadingHistory ? "Loading older messages..." : "Load older messages"}
            </button>
          </div>
        )}

        {messages.length === 0 ? (
          <div className="text-center text-slate-400 text-xs py-20 space-y-1">
            <ShieldCheck className="w-6 h-6 mx-auto text-slate-300" />
            <p className="font-semibold text-slate-600">Encrypted Team Channel</p>
            <p className="text-[10px]">Real-time message push active.</p>
          </div>
        ) : (
          messages.map((m) => {
            const isMe = String(m.sender_id) === String(user?.id) || m.sender === "You" || (m.client_message_id && !m.sender_id);
            const isSelected = selectedIds.has(m.id);

            return (
              <div key={m.id || m.client_message_id} className={`flex items-end gap-1.5 group ${isMe ? "justify-end" : "justify-start"}`}>
                {isSelectMode && (
                  <button onClick={() => setSelectedIds((prev) => { const n = new Set(prev); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; })} className="text-blue-600 mb-1">
                    {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-slate-300" />}
                  </button>
                )}
                {!isSelectMode && (
                  <button onClick={() => handleDelete(m.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-rose-600 transition mb-1" title="Delete for me">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <div className={`flex flex-col max-w-[80%] ${isMe ? "items-end" : "items-start"}`}>
                  <span className="text-[9px] text-slate-400 font-semibold mb-0.5 px-1">
                    {isMe ? "You" : m.sender} • {formatTime(m.created_at)}
                  </span>
                  <div className={`px-3.5 py-2 rounded-2xl text-[12.5px] leading-relaxed shadow-2xs break-words ${isMe ? "bg-blue-600 text-white rounded-br-xs" : "bg-white text-slate-800 border border-slate-200/90 rounded-bl-xs"}`}>
                    {m.text}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {hasUnreadBelow && (
        <button onClick={() => scrollToBottom("smooth")} className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-md flex items-center gap-1">
          <ArrowDown className="w-3.5 h-3.5" /> New messages below
        </button>
      )}

      {/* MESSAGE INPUT */}
      <form onSubmit={handleSend} className="p-3 bg-white border-t border-slate-200 flex items-center gap-2">
        <input
          type="text"
          maxLength={5000}
          placeholder="Type message..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={isSelectMode}
          className="flex-1 bg-slate-100 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium outline-none focus:border-blue-600 focus:bg-white transition"
        />
        <button type="submit" disabled={!inputText.trim() || isSending || isSelectMode} className="p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-40 transition">
          <Send className="w-3.5 h-3.5" />
        </button>
      </form>
    </div>
  );
}